/**
 * Demographic-Adjusted Reference Ranges for Health Scoring
 *
 * All thresholds sourced from peer-reviewed literature.
 * Addresses clinical equity: universal thresholds systematically
 * mis-score certain populations by age, sex, and ethnicity.
 *
 * Citations verified against PubMed via NCBI E-utilities API.
 */

// ── Types ────────────────────────────────────────────────────────────────────

export interface DemographicProfile {
  age?: number | null;
  sex?: 'male' | 'female' | null;
  ethnicity?: string | null;
}

export interface NormRange {
  optimal: [number, number];
  normal: [number, number];
  citation: string;
}

// ── Helpers ──────────────────────────────────────────────────────────────────

/** Get age decade bracket string. */
function ageBracket(age: number): string {
  if (age < 30) return '20-29';
  if (age < 40) return '30-39';
  if (age < 50) return '40-49';
  if (age < 60) return '50-59';
  if (age < 70) return '60-69';
  return '70+';
}

const DEFAULT_AGE = 40;
const DEFAULT_SEX = 'male';

// ── Deep Sleep (minutes) — Age-Adjusted ──────────────────────────────────────
// Ohayon et al. 2004 (PMID: 15586779): deep sleep declines ~2% per decade

const DEEP_SLEEP_NORMS: Record<string, NormRange> = {
  '20-29': { optimal: [60, 120], normal: [45, 135], citation: 'ohayon_2004_sleep' },
  '30-39': { optimal: [50, 110], normal: [35, 125], citation: 'ohayon_2004_sleep' },
  '40-49': { optimal: [40, 100], normal: [25, 115], citation: 'ohayon_2004_sleep' },
  '50-59': { optimal: [30, 85],  normal: [20, 100], citation: 'ohayon_2004_sleep' },
  '60-69': { optimal: [20, 70],  normal: [15, 85],  citation: 'ohayon_2004_sleep' },
  '70+':   { optimal: [15, 60],  normal: [10, 75],  citation: 'ohayon_2004_sleep' },
};

// ── HRV (rMSSD ms) — Age and Sex-Adjusted ───────────────────────────────────
// O'Neal et al. 2016 MESA study (PMID: 27396499)
// Geovanini et al. 2020 Baependi (PMID: 33150136) for broader age ranges

const HRV_NORMS: Record<string, Record<string, NormRange>> = {
  male: {
    '20-29': { optimal: [35, 120], normal: [20, 150], citation: 'geovanini_2020_hrv_age_sex' },
    '30-39': { optimal: [30, 110], normal: [18, 140], citation: 'geovanini_2020_hrv_age_sex' },
    '40-49': { optimal: [25, 95],  normal: [15, 120], citation: 'oneal_2016_mesa_hrv' },
    '50-59': { optimal: [20, 80],  normal: [12, 100], citation: 'oneal_2016_mesa_hrv' },
    '60-69': { optimal: [15, 65],  normal: [8, 85],   citation: 'oneal_2016_mesa_hrv' },
    '70+':   { optimal: [12, 55],  normal: [6, 75],   citation: 'oneal_2016_mesa_hrv' },
  },
  female: {
    '20-29': { optimal: [30, 110], normal: [18, 140], citation: 'geovanini_2020_hrv_age_sex' },
    '30-39': { optimal: [25, 100], normal: [15, 130], citation: 'geovanini_2020_hrv_age_sex' },
    '40-49': { optimal: [22, 85],  normal: [12, 110], citation: 'oneal_2016_mesa_hrv' },
    '50-59': { optimal: [18, 70],  normal: [10, 90],  citation: 'oneal_2016_mesa_hrv' },
    '60-69': { optimal: [14, 58],  normal: [7, 75],   citation: 'oneal_2016_mesa_hrv' },
    '70+':   { optimal: [10, 50],  normal: [5, 65],   citation: 'oneal_2016_mesa_hrv' },
  },
};

// ── Resting Heart Rate — Age and Sex-Adjusted ────────────────────────────────
// Quer et al. 2020 (PMID: 32023264, n=92,457)

const RHR_NORMS: Record<string, Record<string, NormRange>> = {
  male: {
    '20-29': { optimal: [48, 65],  normal: [42, 78],  citation: 'quer_2020_rhr' },
    '30-39': { optimal: [50, 68],  normal: [44, 80],  citation: 'quer_2020_rhr' },
    '40-49': { optimal: [52, 70],  normal: [46, 82],  citation: 'quer_2020_rhr' },
    '50-59': { optimal: [53, 72],  normal: [47, 84],  citation: 'quer_2020_rhr' },
    '60-69': { optimal: [54, 73],  normal: [48, 85],  citation: 'quer_2020_rhr' },
    '70+':   { optimal: [55, 75],  normal: [49, 87],  citation: 'quer_2020_rhr' },
  },
  female: {
    '20-29': { optimal: [52, 70],  normal: [46, 82],  citation: 'quer_2020_rhr' },
    '30-39': { optimal: [54, 72],  normal: [48, 84],  citation: 'quer_2020_rhr' },
    '40-49': { optimal: [55, 74],  normal: [49, 86],  citation: 'quer_2020_rhr' },
    '50-59': { optimal: [56, 76],  normal: [50, 88],  citation: 'quer_2020_rhr' },
    '60-69': { optimal: [57, 77],  normal: [51, 89],  citation: 'quer_2020_rhr' },
    '70+':   { optimal: [58, 78],  normal: [52, 90],  citation: 'quer_2020_rhr' },
  },
};

// ── VO2 Max — Age and Sex-Adjusted ───────────────────────────────────────────
// Kaminsky et al. 2015 FRIEND database (PMID: 26455884)

const VO2MAX_NORMS: Record<string, Record<string, NormRange>> = {
  male: {
    '20-29': { optimal: [44, 99], normal: [36, 99], citation: 'kaminsky_2015_vo2max' },
    '30-39': { optimal: [42, 99], normal: [34, 99], citation: 'kaminsky_2015_vo2max' },
    '40-49': { optimal: [39, 99], normal: [31, 99], citation: 'kaminsky_2015_vo2max' },
    '50-59': { optimal: [35, 99], normal: [28, 99], citation: 'kaminsky_2015_vo2max' },
    '60-69': { optimal: [31, 99], normal: [24, 99], citation: 'kaminsky_2015_vo2max' },
    '70+':   { optimal: [27, 99], normal: [20, 99], citation: 'kaminsky_2015_vo2max' },
  },
  female: {
    '20-29': { optimal: [38, 99], normal: [30, 99], citation: 'kaminsky_2015_vo2max' },
    '30-39': { optimal: [35, 99], normal: [28, 99], citation: 'kaminsky_2015_vo2max' },
    '40-49': { optimal: [32, 99], normal: [25, 99], citation: 'kaminsky_2015_vo2max' },
    '50-59': { optimal: [28, 99], normal: [22, 99], citation: 'kaminsky_2015_vo2max' },
    '60-69': { optimal: [25, 99], normal: [19, 99], citation: 'kaminsky_2015_vo2max' },
    '70+':   { optimal: [22, 99], normal: [16, 99], citation: 'kaminsky_2015_vo2max' },
  },
};

// ── Main Export ──────────────────────────────────────────────────────────────

/**
 * Get demographic-adjusted reference range for a metric.
 * Falls back to median adult norms if demographics missing.
 */
export function getDemographicNorms(
  profile: DemographicProfile,
  metric: 'deep_sleep' | 'hrv' | 'resting_hr' | 'vo2max'
): NormRange {
  const age = profile.age ?? DEFAULT_AGE;
  const sex = profile.sex ?? DEFAULT_SEX;
  const bracket = ageBracket(age);

  switch (metric) {
    case 'deep_sleep':
      return DEEP_SLEEP_NORMS[bracket] ?? DEEP_SLEEP_NORMS['40-49'];

    case 'hrv':
      return HRV_NORMS[sex]?.[bracket] ?? HRV_NORMS[DEFAULT_SEX]['40-49'];

    case 'resting_hr':
      return RHR_NORMS[sex]?.[bracket] ?? RHR_NORMS[DEFAULT_SEX]['40-49'];

    case 'vo2max':
      return VO2MAX_NORMS[sex]?.[bracket] ?? VO2MAX_NORMS[DEFAULT_SEX]['40-49'];

    default:
      return { optimal: [0, 100], normal: [0, 100], citation: '' };
  }
}

/**
 * Normalize a metric value to 0–100 using demographic-adjusted ranges.
 * Within optimal → 80-100, within normal → 40-79, outside → 0-39.
 */
export function normalizeDemographic(value: number, norms: NormRange): number {
  if (value >= norms.optimal[0] && value <= norms.optimal[1]) {
    // Within optimal: score 80-100 based on position within range
    const range = norms.optimal[1] - norms.optimal[0] || 1;
    const center = (norms.optimal[0] + norms.optimal[1]) / 2;
    const distFromCenter = Math.abs(value - center) / (range / 2);
    return Math.round(100 - distFromCenter * 20); // 100 at center, 80 at edges
  }
  if (value >= norms.normal[0] && value <= norms.normal[1]) {
    // Within normal: score 40-79
    const isBelow = value < norms.optimal[0];
    const gap = isBelow ? norms.optimal[0] - norms.normal[0] : norms.normal[1] - norms.optimal[1];
    const dist = isBelow ? norms.optimal[0] - value : value - norms.optimal[1];
    const pct = gap > 0 ? dist / gap : 0;
    return Math.round(79 - pct * 39); // 79 near optimal, 40 at edge of normal
  }
  // Outside normal: score 0-39
  const isBelow = value < norms.normal[0];
  const boundary = isBelow ? norms.normal[0] : norms.normal[1];
  const dist = Math.abs(value - boundary);
  const maxDist = (norms.normal[1] - norms.normal[0]) * 0.5 || 20;
  const severity = Math.min(1, dist / maxDist);
  return Math.round(39 * (1 - severity));
}
